import React from "react";
import { FaExternalLinkAlt, FaLaptopCode } from "react-icons/fa";
import { useLoaderData } from "react-router-dom";
import Loading from "../Loader/Loading";
import "react-photo-view/dist/react-photo-view.css";
import { PhotoProvider, PhotoView } from "react-photo-view";
import backendCommerceCover from "../../Images/cover-backend-commerce.svg";
import posInventoryCover from "../../Images/cover-pos-inventory.svg";
import warrantyServiceCover from "../../Images/cover-warranty-service.svg";
import communityQuizCover from "../../Images/cover-community-quiz.svg";
import donationAnalyticsCover from "../../Images/cover-donation-analytics.svg";
import fullstackServiceCover from "../../Images/cover-fullstack-service.svg";

const FALLBACK_COVERS = [
    backendCommerceCover,
    posInventoryCover,
    warrantyServiceCover,
    communityQuizCover,
    donationAnalyticsCover,
    fullstackServiceCover,
];

const getFallbackByHash = (project = {}) => {
    const key = `${project.id || project._id || ""}${project.name || "project"}`;
    const hash = [...key].reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return FALLBACK_COVERS[hash % FALLBACK_COVERS.length];
};

const getProjectCover = (project = {}) => {
    const searchableText = `${project.name || ""} ${project.description || ""} ${(project.technology || []).join(" ")}`.toLowerCase();

    if (
        searchableText.includes("pos") ||
        searchableText.includes("inventory") ||
        searchableText.includes("sales") ||
        searchableText.includes("supplier")
    ) {
        return posInventoryCover;
    }

    if (
        searchableText.includes("e-commerce") ||
        searchableText.includes("ecommerce") ||
        searchableText.includes("checkout") ||
        searchableText.includes("order") ||
        searchableText.includes("cart")
    ) {
        return backendCommerceCover;
    }

    if (
        searchableText.includes("warranty") ||
        searchableText.includes("claim") ||
        searchableText.includes("qr code") ||
        searchableText.includes("service provider")
    ) {
        return warrantyServiceCover;
    }

    if (
        searchableText.includes("quiz") ||
        searchableText.includes("leaderboard") ||
        searchableText.includes("publications") ||
        searchableText.includes("employee")
    ) {
        return communityQuizCover;
    }

    if (
        searchableText.includes("donation") ||
        searchableText.includes("mojo") ||
        searchableText.includes("products sold")
    ) {
        return donationAnalyticsCover;
    }

    if (
        searchableText.includes("travel") ||
        searchableText.includes("study") ||
        searchableText.includes("learning") ||
        searchableText.includes("react") ||
        searchableText.includes("firebase")
    ) {
        return fullstackServiceCover;
    }

    return getFallbackByHash(project);
};

const ProjectDetails = () => {
    const projectDetail = useLoaderData();

    const {
        name,
        clientRepo,
        serverRepo,
        liveSite,
        technology,
        description,
    } = projectDetail || {};

    const imageSrc = getProjectCover(projectDetail);

    if (!projectDetail) {
        return <Loading />;
    }

    return (
        <div>
            <div className="max-w-[1200px] mx-auto py-20 px-2 md:px-5 lg:px-12  ">
                <div className="grid lg:grid-cols-2 gap-5 justify-items-center items-center">
                    <div
                        className="flex flex-col justify-center items-center"
                        data-aos="fade-right"
                        data-aos-duration="2000"
                    >
                        <div>
                            <PhotoProvider speed={() => 800}>
                                <PhotoView src={imageSrc}>
                                    <img
                                        className="w-full mb-5 hover:cursor-pointer"
                                        src={imageSrc}
                                        alt="projectPicture"
                                    />
                                </PhotoView>
                            </PhotoProvider>
                        </div>
                        <div className="flex gap-6 flex-col md:flex-row">
                            {/* Live Site */}
                            <a
                                href={liveSite}
                                target="_blank"
                                className="transition font-semibold ease-in-out delay-150 border-2 border-orange-500 text-yellow-50 hover:-translate-y-1 hover:scale-110 hover:bg-orange-500 duration-300 p-2 flex items-center"
                                rel="noopener noreferrer"
                            >
                                Live Site <FaExternalLinkAlt className="ml-2" />
                            </a>

                            {/* Client Repository - Only show if it's not null or empty */}
                            {clientRepo && (
                                <a
                                    href={clientRepo}
                                    target="_blank"
                                    className="transition font-semibold ease-in-out delay-150 border-2 border-orange-500 text-yellow-50 hover:-translate-y-1 hover:scale-110 hover:bg-orange-500 duration-300 p-2 flex items-center"
                                    rel="noopener noreferrer"
                                >
                                    Client Side{" "}
                                    <FaLaptopCode className="ml-2" />
                                </a>
                            )}

                            {/* Server Repository - Only show if it's not null or empty */}
                            {serverRepo && (
                                <a
                                    href={serverRepo}
                                    target="_blank"
                                    className="transition font-semibold ease-in-out delay-150 border-2 border-orange-500 text-yellow-50 hover:-translate-y-1 hover:scale-110 hover:bg-orange-500 duration-300 p-2 flex items-center"
                                    rel="noopener noreferrer"
                                >
                                    Server Side{" "}
                                    <FaLaptopCode className="ml-2" />
                                </a>
                            )}
                        </div>
                    </div>

                    <div
                        className="mx-2"
                        data-aos="fade-left"
                        data-aos-duration="2000"
                    >
                        <div className="flex items-center mb-5 gap-5">
                            <p className="text-left text-4xl text-orange-400">
                                {name}
                            </p>
                        </div>
                        <p className="text-yellow-50">{description}</p>
                        <div className="mt-5 pb-16">
                            <p className="text-orange-400 font-bold my-5">
                                Technology Used{" "}
                            </p>
                            <div className="flex flex-wrap gap-3 text-white">
                                {technology.map((t, index) => (
                                    <p
                                        key={index}
                                        className="transition bg-slate-800 ease-in-out delay-150 text-yellow-50 hover:-translate-y-1 hover:scale-110 hover:bg-orange-500 duration-300 p-2 flex items-center px-5 text-sm py-1 rounded-lg"
                                    >
                                        {t}
                                    </p>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectDetails;
